<template>
    <div class="space-y-3">
        <section class="space-y-1">
            <form-label>Source</form-label>
            <div v-if="sourceObject" class="flex items-center justify-between gap-2 text-xs">
                <button v-if="gpioSource" class="truncate text-left hover:text-ui-active" title="Select the GPIO entity node"
                    @click="logic.select(page, 'node', gpioSource.id)">{{ sourceLabel }}</button>
                <span v-else class="truncate">{{ sourceLabel }}</span>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the source"
                    @click="logic.removeWire(page, sourceWire.id)">×</button>
            </div>
            <p v-else class="text-xs text-gray-300">Drag a wire from a UI object's or GPIO entity's port to this event.</p>
        </section>

        <form-select v-if="sourceObject" name="Event" :model-value="node.data.event"
            @update:model-value="event => logic.updateNodeData(page, node.id, { event })">
            <option value="" disabled>Select an event</option>
            <option v-for="choice in choices" :key="choice.event" :value="choice.event" :disabled="choice.disabled">
                {{ title(choice.event) }}{{ choice.disabled ? ' (in use)' : '' }}
            </option>
        </form-select>

        <section v-if="variables.length" class="space-y-1">
            <form-label>Event values</form-label>
            <ul class="space-y-0.5 text-xs text-gray-200">
                <li v-for="variable in variables" :key="variable.id">
                    <code class="text-ui-active">{{ variable.token }}</code> — {{ variable.label }}
                </li>
            </ul>
        </section>

        <section class="space-y-1">
            <form-label>Actions (in execution order)</form-label>
            <p v-if="!actions.length" class="text-xs text-gray-300">Drag a wire from this event's output to an action.</p>
            <div v-for="(item, index) in actions" :key="item.wire.id"
                class="flex items-center gap-1 rounded border border-white/20 bg-white/5 px-1 py-0.5 text-xs">
                <button class="min-w-0 flex-1 truncate text-left hover:text-ui-active" :title="item.view.summary"
                    @click="logic.select(page, 'node', item.node.id)">
                    {{ item.view.icon }} {{ item.view.title }} <span class="text-gray-400">{{ item.view.summary }}</span>
                </button>
                <button class="text-gray-300 hover:text-ui-active disabled:opacity-30" :disabled="index === 0"
                    title="Run earlier" @click="move(index, -1)">↑</button>
                <button class="text-gray-300 hover:text-ui-active disabled:opacity-30" :disabled="index === actions.length - 1"
                    title="Run later" @click="move(index, 1)">↓</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect this action"
                    @click="logic.removeWire(page, item.wire.id)">×</button>
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useLogicStore } from '../../stores/logicStore.js';
import {
    findNode,
    getEventActionWires,
    getEventNodeChoices,
    getEventSourceWire,
} from '../../logic/logicGraph.js';
import { describeLogicNode, eventLabel } from '../../logic/logicPresentation.js';
import { getEventContextVariables } from '../../actions/actionContextResolver.js';
import { gpioEditorSource } from '../../logic/gpioLogic.js';

const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
});
const logic = useLogicStore();

const sourceWire = computed(() => getEventSourceWire(props.graph, props.node.id));
const gpioSource = computed(() => (sourceWire.value?.from.node !== undefined
    ? findNode(props.graph, sourceWire.value.from.node) : null));
// A GPIO source is presented to the event-value lookup as a stand-in object.
const sourceObject = computed(() => {
    if (gpioSource.value) return gpioEditorSource(props.page, gpioSource.value);
    return sourceWire.value ? props.context.getObject(sourceWire.value.from.object) : null;
});
const sourceLabel = computed(() => {
    if (gpioSource.value) return `GPIO ${gpioSource.value.data.entity || '(no entity selected)'}`;
    const object = sourceObject.value;
    if (object.obj === 'page') return `page p${object.haspid}`;
    const name = object.comment ? ` — ${object.comment}` : '';
    return `${object.obj} p${props.page.haspid}b${object.haspid}${name}`;
});
const choices = computed(() => getEventNodeChoices(props.graph, props.node.id, props.context));
const variables = computed(() => (sourceObject.value && props.node.data.event
    ? getEventContextVariables(sourceObject.value, props.node.data.event) : []));
const actions = computed(() => getEventActionWires(props.graph, props.node.id)
    .map(wire => ({ wire, node: findNode(props.graph, wire.to.node) }))
    .filter(item => item.node)
    .map(item => ({ ...item, view: describeLogicNode(item.node) })));

const title = value => {
    const label = eventLabel(value);
    return label.charAt(0).toUpperCase() + label.slice(1);
};

function move(index, delta) {
    const ids = actions.value.map(item => item.wire.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + delta, 0, moved);
    logic.reorderEventActions(props.page, props.node.id, ids);
}
</script>
